import {
	Component,
	ElementRef,
	Input,
	OnDestroy,
	ViewChild,
	ViewEncapsulation,
	OnInit,
	AfterViewInit,
	Output,
	EventEmitter,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor, UpperCasePipe } from '@angular/common';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { User } from '../../../shared/models/user';
import { UserService } from '../../../shared/services/user.service';
import { UNDERSCORE } from '../../../shared/utils/constant';
import { safe } from '../../../shared/utils/safe';
import { Guarantee } from '../../models/guarantee';
import { GuaranteeRequestAttachment } from '../../models/guarantee-request-attachment';
import {
	ATTACHMENT_CATEGORY_OTHER,
	ATTACHMENT_CATEGORY_SOIL,
	ATTACHMENT_CATEGORY_WATER,
	ATTACHMENT_CATEGORY_WEBFORM
} from '../../models/guarantee-constants';
import { GuaranteeRequestService } from '../../services/guarantee-request.service';
import { TranslationService } from '../../../shared/services/translation.service';
import { SafeUrlPipe } from '../../../shared/pipes/safeurl.pipe';

@Component({
	selector: 'copo-guarantee-request-summary',
	templateUrl: './guarantee-request-summary.component.html',
	styleUrl: './guarantee-request-summary.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [NgIf, TranslateModule, NgFor, MatIconModule, UpperCasePipe, SafeUrlPipe]
})
export class GuaranteeRequestSummaryComponent implements OnDestroy, OnInit, AfterViewInit {
	@ViewChild('customerInformation', { read: ElementRef }) customerInformation!: ElementRef<HTMLElement>;
	@ViewChild('constructionSite', { read: ElementRef }) constructionSite!: ElementRef<HTMLElement>;
	@ViewChild('projectLocation', { read: ElementRef }) projectLocation!: ElementRef<HTMLElement>;
	@ViewChild('atmosphericCorrosion', { read: ElementRef }) atmosphericCorrosion!: ElementRef<HTMLElement>;
	@ViewChild('typesOfGuarantee', { read: ElementRef }) typesOfGuarantee!: ElementRef<HTMLElement>;
	@ViewChild('organicCoatingRoofing', { read: ElementRef }) organicCoatingRoofing!: ElementRef<HTMLElement>;
	@ViewChild('ocRoofingSandwichPanel', { read: ElementRef }) ocRoofingSandwichPanel!: ElementRef<HTMLElement>;
	@ViewChild('ocCladding', { read: ElementRef }) ocCladding!: ElementRef<HTMLElement>;
	@ViewChild('ocCladdingSandwichPanel', { read: ElementRef }) ocCladdingSandwichPanel!: ElementRef<HTMLElement>;
	@ViewChild('ocRoofingCladdingOthers', { read: ElementRef }) ocRoofingCladdingOthers!: ElementRef<HTMLElement>;
	@ViewChild('guaranteeParts', { read: ElementRef }) guaranteeParts!: ElementRef<HTMLElement>;
	@ViewChild('otherMaterials', { read: ElementRef }) otherMaterials!: ElementRef<HTMLElement>;
	@ViewChild('combinedPartsAndOthers', { read: ElementRef }) combinedPartsAndOthers!: ElementRef<HTMLElement>;
	@Input() guarantee?: Guarantee;
	@Input() legacyAttachmentDisplay = false;
	@Input() showAdditionalDocuments = false;
	@Output() readonly summaryElementsCreated = new EventEmitter<HTMLElement[]>();

	submitting = false;
	user: User;
	soldToName = '';
	readonly UNDERSCORE = UNDERSCORE;

	private guaranteeIdSub?: Subscription;
	constructor(
		private readonly guaranteeService: GuaranteeRequestService,
		private readonly userService: UserService,
		private readonly translationService: TranslationService,

	) {
		this.user = this.userService.user;
	}

	ngOnInit() {
		if (!this.guarantee?.soldTo) {
			return;
		}
		this.guaranteeIdSub = this.userService.getBocs(
			['name', 'businessId'],
			`businessId:${ this.guarantee.soldTo }`
		).subscribe({
			next: soldToList => {
				const soldTos = soldToList.results;

				if (soldTos.length !== 0) {
					this.soldToName = soldTos[0].name;
				}
			}
		});
	}

	ngAfterViewInit() {
		if (!this.guarantee) {
			return;
		}
		this.summaryElementsCreated.emit((
			this.guarantee.applicationType === 'Organic Coating'
				? this.getOcSections(this.guarantee)
				: this.guarantee.parts.length === 0
					? [
						this.customerInformation,
						this.constructionSite,
						this.projectLocation,
						this.atmosphericCorrosion,
						this.combinedPartsAndOthers,
					]
					: [
						this.customerInformation,
						this.constructionSite,
						this.projectLocation,
						this.atmosphericCorrosion,
						this.otherMaterials,
						this.guaranteeParts
					])
			.map(elRef => elRef.nativeElement));
	}

	getAttachments(guarantee: Guarantee) {
		return Object.values(guarantee.attachments);
	}

	getSoilAnalysisAttachments(guarantee: Guarantee) {
		return this.getAttachments(guarantee).filter(
			attachment => !attachment.deleted && attachment.metaData.type === ATTACHMENT_CATEGORY_SOIL
		);
	}

	getAttachmentUrl(id: string, attachment: GuaranteeRequestAttachment) {
		return attachment.localUrl
			|| this.guaranteeService.getDownloadFile(id, safe(attachment.chronicleId));
	}

	async showAttachment(event: MouseEvent, attachment: GuaranteeRequestAttachment) {
		await this.guaranteeService.showAttachment(event, attachment);
	}

	getWaterAnalysisAttachments(guarantee: Guarantee) {
		return this.getAttachments(guarantee).filter(
			attachment => !attachment.deleted && attachment.metaData.type === ATTACHMENT_CATEGORY_WATER
		);
	}

	getWaterAnalysisAttachmentUrl(id: string, attachment: GuaranteeRequestAttachment) {
		return attachment.localUrl
			|| this.guaranteeService.getDownloadFile(id, safe(attachment.chronicleId));
	}

	getOtherAttachments(guarantee: Guarantee) {
		return this.getAttachments(guarantee).filter(
			attachment => !attachment.deleted
				&& (
					attachment.metaData.environment === 'STU'
						? attachment.metaData.type !== ATTACHMENT_CATEGORY_SOIL
						&& attachment.metaData.type !== ATTACHMENT_CATEGORY_WATER
						: attachment.metaData.type === ATTACHMENT_CATEGORY_OTHER || !attachment.metaData.type
				)
		);
	}

	isWebformAttachment(attachment: GuaranteeRequestAttachment) {
		return attachment.metaData.type === ATTACHMENT_CATEGORY_WEBFORM;
	}

	ngOnDestroy() {
		this.guaranteeIdSub?.unsubscribe();
	}

	getLongType(appType: string) {
		switch (appType) {
		case 'Magnelis-Solar':
			return ` ${ this.translationService.getTR('guarantee.magnelissolar.long') }`;
		case 'Magnelis-Building':
			return ` ${ this.translationService.getTR('guarantee.magnelisbuilding.long') }`;
		case 'Magnelis-Other application':
			return ` ${ this.translationService.getTR('guarantee.magnelisothers.long') }`;
		case 'Organic Coating':
			return ` ${ this.translationService.getTR('guarantee.organiccoating.long') }`;
		}
		return '';
	}

	getOcSections(guarantee: Guarantee) {
		const ocSections = [
			this.customerInformation,
			this.constructionSite,
			this.projectLocation,
			this.typesOfGuarantee
		];
		if (guarantee.roofingDeckingRoof || guarantee.roofingSingleSkinRoof || guarantee.roofingDoubleSkinRoof) {
			ocSections.push(this.organicCoatingRoofing);
		}
		if (guarantee.ocRoofing || guarantee.roofingSandwichPanelsBuiltUpSys) {
			ocSections.push(this.ocRoofingSandwichPanel);
		}
		if (guarantee.cladSingleSkin || guarantee.cladDoubleSkin) {
			ocSections.push(this.ocCladding);
		}
		if (guarantee.clad || guarantee.cladSandwichPanelsBuiltUpSys) {
			ocSections.push(this.ocCladdingSandwichPanel);
		}
		ocSections.push(this.ocRoofingCladdingOthers, this.guaranteeParts);
		return ocSections;
	}
}
